/**
 * Top-center, route-independent notification bar for background scans.
 * Rendered once at the App root — visible on every page while a scan started
 * anywhere (onboarding submit, AI details rescan, etc.) is still running, and
 * shows a completion/failure/partial toast for a few seconds afterwards.
 */
import { Box, Chip, CircularProgress, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircle2, X, AlertTriangle, XCircle } from 'lucide-react';
import { useScan } from '@/lib/ScanContext';

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function ScanNotificationBar() {
  const theme = useTheme();
  const { scans, toasts, dismissToast } = useScan();

  if (scans.length === 0 && toasts.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 68,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        pointerEvents: 'none',
      }}
    >
      {scans.map((s) => (
        <Box
          key={s.meta.key}
          sx={{
            pointerEvents: 'auto',
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.75, py: 0.85, borderRadius: 10,
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.3),
            boxShadow: theme.shadows[6],
          }}
        >
          <CircularProgress size={14} thickness={5} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}>{s.meta.label}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{s.message}</Typography>
          <Chip label={formatElapsed(s.elapsedMs)} size="small" sx={{ fontSize: 11, height: 20, fontFamily: 'monospace' }} />
        </Box>
      ))}

      {toasts.map((t) => (
        <Stack
          key={t.id}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            pointerEvents: 'auto',
            px: 1.75, py: 0.85, borderRadius: 10,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor:
              t.severity === 'success' ? alpha(theme.palette.success.main, 0.7)
              : t.severity === 'warning' ? alpha(theme.palette.warning.main, 0.7)
              : alpha(theme.palette.error.main, 0.7),
            boxShadow: theme.shadows[6],
          }}
        >
          {t.severity === 'success' ? <CheckCircle2 size={15} color={theme.palette.success.main} />
            : t.severity === 'warning' ? <AlertTriangle size={15} color={theme.palette.warning.main} />
            : <XCircle size={15} color={theme.palette.error.main} />}
          <Typography sx={{ fontSize: 12.5, maxWidth: 480, color: 'text.primary' }}>{t.message}</Typography>
          <IconButton size="small" onClick={() => dismissToast(t.id)} sx={{ p: 0.25, color: 'text.secondary' }}>
            <X size={13} />
          </IconButton>
        </Stack>
      ))}
    </Box>
  );
}
